import { Navigate, Outlet, useNavigate } from "react-router-dom"
import { useAuth } from "../auth"
import { Suspense, useMemo, useState } from "react"
import Stack from "@mui/system/Stack"
import { AppBar, Avatar, IconButton, MenuItem, Select, SelectChangeEvent, Toolbar, Typography } from "@mui/material"
import MenuIcon from "@mui/icons-material/Menu"
import { Box } from "@mui/system"
import LeftMenu from "./LeftMenu"
import { useTranslation } from "react-i18next"
import LangLink from "./LangLink"
import { useIsMobile } from "../hooks/mobile"
import { Close } from "@mui/icons-material"
import MenuMobile from "./MenuMobile"

type AuthLayoutProps = {
  redirect_to: string
}

function LanguageSelector() {
  const { i18n } = useTranslation()
  const [language, setLanguage] = useState(i18n.language)
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  const handleChange = (event: SelectChangeEvent<string>) => {
    const newLanguage = event.target.value as string
    setLanguage(newLanguage)
    i18n.changeLanguage(newLanguage)
    // we have to change the language in the url
    const currentPath = window.location.pathname
    let newPath = currentPath.replace(/^\/(it|en)(\/|$)/, `/${newLanguage}$2`)
    if (newPath === currentPath) {
      newPath = `/${newLanguage}${currentPath}`
    }
    navigate(newPath, { replace: true })
  }

  return (
    <Select
      value={language}
      onChange={handleChange}
      sx={{ mr: 2, backgroundColor: "white", borderRadius: 1, width: isMobile ? 50 : 150 }}
      variant="outlined"
      size="small"
      // dont show the arrow icon if we are in mobile
      IconComponent={isMobile ? () => null : undefined}
    >
      <MenuItem value="it">
        <img src="/icons/it.svg" height={14} style={{ marginRight: 10 }} /> {!isMobile && "Italiano"}
      </MenuItem>
      <MenuItem value="en">
        <img src="/icons/en.svg" height={14} style={{ marginRight: 10 }} />
        {!isMobile && "English"}
      </MenuItem>
    </Select>
  )
}

export function AuthLayout({ redirect_to }: AuthLayoutProps) {
  const auth = useAuth()
  const { t } = useTranslation()
  const [menuClosed, setMenuClosed] = useState(false)
  const isMobile = useIsMobile()
  const [menuMobile, setMenuMobile] = useState(false)

  const lettersFromFullName = useMemo(() => {
    if (!auth.user?.full_name) return ""
    const names = auth.user.full_name.split(" ")
    const initials = names.map((name) => name[0]).join("")
    return initials
  }, [auth.user?.full_name])

  if (!auth.user) {
    return <Navigate to={redirect_to} replace />
  }

  return (
    <Stack direction="column" sx={{ height: "100vh" }}>
      <AppBar variant="outlined" position="static" color="transparent">
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={() => {
              if (isMobile) {
                setMenuMobile((prev: boolean) => !prev)
              } else {
                setMenuClosed((prev: boolean) => !prev)
              }
            }}
          >
            {isMobile && menuMobile ? <Close /> : <MenuIcon />}
          </IconButton>
          <Typography variant={isMobile ? "subtitle1" : "h6"} component="div" sx={{ flexGrow: 1 }}>
            {t("application")}
          </Typography>
          <Box display={"flex"} alignItems={"center"}>
            {/* change language selector */}
            <LanguageSelector />
            <LangLink to="/profilo" style={{ textDecoration: "none", color: "inherit" }}>
              <Box flexGrow={1} display="flex" alignItems="center">
                <Avatar sx={{ mr: 2, backgroundColor: "primary.main" }}>{lettersFromFullName}</Avatar>
                <Stack direction="column" sx={{ display: { xs: "none", md: "flex" } }}>
                  <Typography variant="subtitle2" component="div" sx={{ flexGrow: 1 }}>
                    {auth.user?.email}
                  </Typography>
                  <Typography variant="subtitle2" component="div" sx={{ flexGrow: 1 }}>
                    {t(`user.enum_ruolo.${auth.user.ruolo}`)}
                  </Typography>
                </Stack>
              </Box>
            </LangLink>
          </Box>
        </Toolbar>
      </AppBar>
      {isMobile && <MenuMobile isOpen={menuMobile} setIsOpen={setMenuMobile} />}
      <Stack direction="row" flexGrow={1} zIndex={1} sx={{ overflow: "auto" }}>
        {!isMobile && <LeftMenu menuClosed={menuClosed} />}
        <Box flexGrow={1} sx={{ width: "100%", overflow: "hidden", maxWidth: "100%" }}>
          <Suspense fallback={<div>{t("general.caricamento")}</div>}>
            <Outlet />
          </Suspense>
        </Box>
      </Stack>
    </Stack>
  )
}
